<template>
  <div id="ChangeRouteOrigin" class="route-origin-view">
    <Title
      title="Cambiar origen de ruta"
      subtitle="Busca una ruta y modifica su origen."
    />

    <div class="content">
      <section class="section-block">
        <div class="section-header">
          <span class="section-step">1</span>
          <span class="section-title">BUSCAR RUTA II</span>
        </div>

        <div class="search-row">
          <el-input
            ref="routeInput"
            v-model="searchRouteId"
            class="route-input"
            :disabled="searchingRoute"
            placeholder="Ruta Ejemplo: 11549"
            prefix-icon="el-icon-search"
            @keyup.enter.native="searchRoute"
          />
          <el-button
            type="success"
            class="search-button"
            :loading="searchingRoute"
            @click="searchRoute"
          >
            <i class="el-icon-check" />
            BUSCAR
          </el-button>
        </div>
      </section>

      <template v-if="showRouteData">
        <section class="section-block info-block">
          <div class="section-header">
            <span class="section-title">INFORMACION DE LA RUTA</span>
          </div>

          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">ID Seguimiento:</span>
              <span class="info-value">{{ routeData.planningId }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">ID Ruta:</span>
              <span class="info-value">{{ routeData.routeIdLabel }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Estado:</span>
              <span class="info-value">{{ routeData.status }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Usuario:</span>
              <span class="info-value">{{ routeData.user }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Fecha Ruta:</span>
              <span class="info-value">{{ routeData.date }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Total Rutas:</span>
              <span class="info-value">{{ routeData.totalRoutes }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Total SOC:</span>
              <span class="info-value">{{ routeData.totalSuborders }}</span>
            </div>
          </div>

          <div class="current-origin-wrapper">
            <p class="field-label">Origen Actual:</p>
            <div class="current-origin">
              {{ routeData.currentOrigin }}
            </div>
          </div>
        </section>

        <section class="section-block">
          <div class="section-header">
            <span class="section-title">CAMBIO DE ORIGEN DE RUTA</span>
          </div>

          <div class="form-field">
            <label class="field-label" for="new-origin-select">Nuevo Origen:</label>
            <el-select
              id="new-origin-select"
              v-model="selectedOrigin"
              class="origin-select"
              filterable
              :loading="loadingOrigins"
              placeholder="Escribe para buscar origen..."
            >
              <el-option
                v-for="origin in originOptions"
                :key="origin.value"
                :label="origin.label"
                :value="origin.value"
              />
            </el-select>
          </div>

          <el-button
            type="success"
            class="save-button"
            :disabled="!selectedOrigin || !routeData.routeId"
            :loading="savingOriginChange"
            @click="saveOriginChange()"
          >
            GUARDAR CAMBIO DE ORIGEN
          </el-button>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { apiRequest } from '@/backend/vue-axios/axios';
import Title from '../components/base/Title.vue';

const dummyRoute = {
  planningId: 11549,
  routeId: 3536173,
  routeIdLabel: '3536173',
  status: 'Finalizada',
  user: 'admin@falabella.cl',
  date: '10-03-2026',
  totalRoutes: 1,
  totalSuborders: 1,
  currentOrigin: 'HUB XD',
};

export default {
  name: 'ChangeRouteOrigin',
  components: {
    Title,
  },
  computed: {
    notificationRetryDelays() {
      return [1000, 2000, 4000];
    },
  },
  data() {
    return {
      searchRouteId: '11549',
      searchingRoute: false,
      savingOriginChange: false,
      loadingOrigins: false,
      showRouteData: true,
      selectedOrigin: '',
      routeData: { ...dummyRoute },
      originOptions: [],
    };
  },
  mounted() {
    this.$refs.routeInput.focus();
    this.getOriginOptions();
  },
  methods: {
    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async notifyRouteAssociationUpdateEvent(routeId) {
      for (let attempt = 0; attempt < this.notificationRetryDelays.length; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await apiRequest({
            module: 'mobile',
            service: 'retry_route_association_update_event',
            pathparam: {
              id: routeId,
            },
          });

          return true;
        } catch (error) {
          const isLastAttempt = attempt === this.notificationRetryDelays.length - 1;

          if (isLastAttempt) {
            this.$alert(
              `El origen de la ruta ${routeId} se actualizo, pero no se pudo notificar el cambio.`,
              'Notificacion pendiente',
              {
                type: 'warning',
                confirmButtonText: 'Entendido',
              },
            );
            return false;
          }

          // eslint-disable-next-line no-await-in-loop
          await this.wait(this.notificationRetryDelays[attempt]);
        }
      }

      return false;
    },
    getOriginOptions() {
      this.loadingOrigins = true;

      apiRequest({
        module: 'planning',
        service: 'distribution_centers',
      }).then((response) => {
        this.originOptions = response.data
          .filter(distributionCenter => distributionCenter.id && distributionCenter.name)
          .map(distributionCenter => ({
            value: distributionCenter.id,
            label: distributionCenter.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }).catch(() => {
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'No se pudo cargar la lista de origenes.',
        });
      }).finally(() => {
        this.loadingOrigins = false;
      });
    },
    mapRouteResponseToView(route) {
      const routeId = route.id || route.route_id || route.routes_id || null;
      const planningStatus = route.planning_status || {};
      const planningAccount = planningStatus.account || {};
      const planningId = route.planning_id
        || route.planningId
        || route.planningstatusesid
        || route.planning_statuses_id
        || planningStatus.id
        || null;
      const currentOrigin = route.distribution_center_name
        || route.distribution_centers_name
        || (route.distribution_center && route.distribution_center.name)
        || (route.distribution_center && route.distribution_center.name)
        || 'Sin origen';
      const totalSuborders = route.total_suborders
        || route.totalSuborders
        || (Array.isArray(route.delivery_points) ? route.delivery_points.length : 0);
      const routeStatus = route.status
        || planningStatus.status
        || (route.finalized ? 'Finalizada' : null)
        || (route.active ? 'Activa' : null)
        || 'No informado';
      const routeUser = route.email
        || route.user_email
        || route.user
        || planningAccount.email
        || 'No informado';
      const routeDate = route.created_at
        || planningStatus.created_at
        || route.start_time
        || null;

      return {
        planningId,
        routeId,
        routeIdLabel: routeId ? `${routeId}` : 'Sin ruta',
        status: routeStatus,
        user: routeUser,
        date: routeDate ? moment(routeDate).format('DD-MM-YYYY') : 'Sin fecha',
        totalRoutes: 1,
        totalSuborders,
        currentOrigin,
      };
    },
    normalizeRouteResponse(responseData) {
      if (Array.isArray(responseData)) {
        return responseData;
      }

      if (responseData && Array.isArray(responseData.routes)) {
        return responseData.routes;
      }

      return responseData ? [responseData] : [];
    },
    searchRoute() {
      if (!this.searchRouteId) {
        this.$message.warning('Ingresa un id de ruta para buscar.');
        return;
      }

      this.searchingRoute = true;
      const routeId = Number(this.searchRouteId);
      const searchedRouteId = Number.isNaN(routeId) ? this.searchRouteId : routeId;

      apiRequest({
        module: 'planning',
        service: 'get_route_by_id',
        pathparam: {
          id: this.searchRouteId,
        },
      }).then((response) => {
        const routes = this.normalizeRouteResponse(response.data);
        const matchedRoute = routes.find(route => (
          `${route.id || route.route_id || route.routes_id}` === `${searchedRouteId}`
        ));

        if (!matchedRoute) {
          this.showRouteData = false;
          this.$message.warning('No se encontró información para la ruta consultada.');
          return null;
        }

        this.routeData = this.mapRouteResponseToView(matchedRoute);
        this.showRouteData = true;
        this.selectedOrigin = '';
        return null;
      }).catch((error) => {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'No fue posible consultar la ruta.';

        this.$message.error(message);
      })
        .finally(() => {
          this.searchingRoute = false;
        });
    },
    isConfirmationRequired(error) {
      return error.response
        && error.response.status === 409
        && error.response.data
        && error.response.data.requires_confirmation;
    },
    confirmForcedOriginChange() {
      return this.$confirm(
        `La ruta ${this.routeData.routeId} tiene un estado distinto al inicial. ¿Seguro que quieres cambiar el origen de todas formas?`,
        'Confirmar cambio de origen',
        {
          confirmButtonText: 'Si, cambiar origen',
          cancelButtonText: 'Cancelar',
          type: 'warning',
        },
      );
    },
    handleOriginChangeSuccess(selectedOption) {
      this.routeData.currentOrigin = selectedOption.label;
      this.$message({
        type: 'success',
        message: `Origen actualizado en la ruta ${this.routeData.routeId}.`,
      });

      this.notifyRouteAssociationUpdateEvent(this.routeData.routeId);
    },
    saveOriginChange(force = false) {
      const selectedOption = this.originOptions
        .find(origin => origin.value === this.selectedOrigin);

      if (!selectedOption) {
        this.$message.warning('Selecciona un nuevo origen.');
        return;
      }

      if (!this.routeData.routeId) {
        this.$message.warning('No se pudo resolver el id de ruta para aplicar el cambio.');
        return;
      }

      this.savingOriginChange = true;

      apiRequest({
        module: 'planning',
        service: 'change_route_distribution_center',
        pathparam: {
          id: this.routeData.routeId,
        },
        params: {
          distribution_center_id: this.selectedOrigin,
          force,
        },
      }).then(() => {
        this.handleOriginChangeSuccess(selectedOption);
      }).catch((error) => {
        if (!force && this.isConfirmationRequired(error)) {
          this.confirmForcedOriginChange()
            .then(() => this.saveOriginChange(true))
            .catch(() => {});
          return;
        }

        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'No fue posible cambiar el origen de la ruta.';

        this.$message.error(message);
      }).finally(() => {
        this.savingOriginChange = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.route-origin-view {
  color: $fb-grey-dark-50;
}

.section-block {
  margin-bottom: 22px;
  padding-top: 6px;
  border-top: 1px solid $fb-grey-light-84;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.section-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  border: 2px solid $fb-green;
  border-radius: 50%;
  color: $fb-green;
  font-size: 12px;
  font-weight: 700;
}

.section-title {
  color: $fb-grey-dark-50;
  font-size: 14px;
  font-weight: 700;
}

.search-row {
  display: flex;
  align-items: center;
  max-width: 720px;
}

.route-input {
  max-width: 460px;
  margin-right: 4px;
}

.search-button,
.save-button {
  min-width: 190px;
  font-weight: 700;
}

.info-block {
  background: linear-gradient(180deg, #fbfbfb 0%, #f4f6f8 100%);
}

.info-grid {
  max-width: 720px;
  border: 1px solid $fb-grey-light-92;
  border-bottom: 0;
  background-color: white;
}

.info-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  min-height: 40px;
  border-bottom: 1px solid $fb-grey-light-92;
}

.info-label,
.info-value {
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 14px;
}

.info-label,
.field-label {
  color: $fb-grey-dark-50;
  font-weight: 700;
}

.current-origin-wrapper {
  max-width: 720px;
  margin-top: 18px;
}

.current-origin {
  display: flex;
  align-items: center;
  min-height: 38px;
  max-width: 420px;
  padding: 0 12px;
  border: 1px solid $fb-grey-light-84;
  background-color: white;
  font-size: 14px;
}

.form-field {
  max-width: 420px;
  margin-bottom: 20px;
}

.field-label {
  display: block;
  margin: 0 0 8px;
  font-size: 14px;
}

.origin-select {
  width: 100%;
}

::v-deep .route-input .el-input__inner,
::v-deep .origin-select .el-input__inner {
  border-color: $fb-grey-light-84;
}

::v-deep .search-button.el-button--success,
::v-deep .save-button.el-button--success {
  border-color: $fb-green;
  background-color: $fb-green;
}

@media (max-width: 768px) {
  .search-row {
    flex-direction: column;
    align-items: stretch;
  }

  .route-input {
    max-width: none;
    margin-right: 0;
    margin-bottom: 12px;
  }

  .search-button,
  .save-button {
    width: 100%;
    min-width: 0;
  }

  .info-row {
    grid-template-columns: 1fr;
    padding: 8px 0;
  }

  .info-label,
  .info-value {
    padding: 4px 12px;
  }

  .current-origin,
  .form-field {
    max-width: none;
  }
}
</style>
